import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { getFilterableTradeHistory } from '../../../redux/slices/trades';
import { dispatch } from '../../../redux/store';

// eslint-disable-next-line react/prop-types
const TradeHistoryFilterCard = ({ onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOption, setSortOption] = useState('createdAt');
  const [nseChecked, setNseChecked] = useState(true);
  const [mcxChecked, setMcxChecked] = useState(true);
  const [sortingOrder, setSortingOrder] = useState('asc');
  const [dateError, setDateError] = useState('');
  const [segmentError, setSegmentError] = useState(false);
  const [apicallFail, setApiCallFail] = useState(false);
  const theme = createTheme({
    palette: {
      primary: { main: '#1976d2' },
      background: { paper: '#fff', default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      h6: {
        fontSize: '1.1rem',
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
      '@media (max-width:600px)': {
        fontSize: 13,
        h6: {
          fontSize: '1rem',
        },
        subtitle1: {
          fontSize: '0.85rem',
        },
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            borderRadius: 8,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 8,
          },
        },
      },
    },
  });

  const handleApplyFilter = async () => {
    setDateError('');
    setApiCallFail(false);
    setSegmentError(false);

    if ((startDate && !endDate) || (!startDate && endDate)) {
      setDateError('Both start and end dates are required.');
      return;
    }

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const selectedStartDate = new Date(startDate);
    const selectedEndDate = new Date(endDate);

    if (startDate && endDate) {
      if (selectedStartDate < thirtyDaysAgo || selectedEndDate > today) {
        setDateError('Date range must be within the last 30 days.');
        return;
      }

      if (selectedEndDate < selectedStartDate) {
        setDateError('End date must be greater than start date.');
        return;
      }
    }

    if (nseChecked === false && mcxChecked === false) {
      setSegmentError(true);
      return;
    }

    const finalParams = {
      sortKey: sortOption,
      sortOrder: sortingOrder,
      nseChecked: nseChecked || false,
      mcxChecked: mcxChecked || false,
    };

    if (startDate && endDate) {
      finalParams.startDate = startDate;
      finalParams.endDate = endDate;
    }

    // localStorage.setItem('tradeHistoryFilters', JSON.stringify(finalParams));
    let errorFlag = false;
    try {
      await dispatch(getFilterableTradeHistory(finalParams));
    } catch (error) {
      errorFlag = true;
      console.log(errorFlag);
    }
    if (errorFlag === true) {
      setApiCallFail(true);
      return;
    }
    onClose();
  };

  const handleResetFilter = () => {
    setStartDate('');
    setEndDate('');
    setSortOption('createdAt');
    setSortingOrder('asc');
    setNseChecked(true);
    setMcxChecked(true);
    setDateError('');
    setSegmentError(false);
    setApiCallFail(false);
    // localStorage.removeItem('tradeHistoryFilters');
  };

  const handleSegmentChange = (segmentSetter, segmentChecked) => {
    segmentSetter(segmentChecked);
    if (segmentChecked) {
      setSegmentError(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: { xs: 1.5, sm: 2.5 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxWidth: { xs: '100%', sm: '600px', md: '800px' },
          width: '95%',
          margin: 'auto',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <AppBar
          position="static"
          sx={{
            marginBottom: { xs: 2, sm: 3 },
            background: '#1976d2',
            boxShadow: 'none',
            borderRadius: '6px',
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              minHeight: { xs: '48px', sm: '56px' },
              px: { xs: 2, sm: 3 }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.1rem' },
              }}
            >
              Filter Data
            </Typography>
          </Toolbar>
        </AppBar>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={12} sm={6}>
            <TextField type="date"
              label="START DATE"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (dateError) setDateError('');
              }}
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  height: { sm: '42px' }
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField type="date"
              label="END DATE"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (dateError) setDateError('');
              }}
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  height: { sm: '42px' }
                },
              }}
            />
          </Grid>
        </Grid>

        {dateError && (
          <Typography
            color="error"
            sx={{
              mb: 2,
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '8px',
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              borderRadius: '8px',
            }}
          >
            {dateError}
          </Typography>
        )}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select
                label="SORT BY"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                fullWidth
                size="small"
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    height: { sm: '42px' }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              >
                <option value="createdAt">Last Updated</option>
                <option value="script.tradingsymbol">A to Z</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select
                label="SORTING ORDER"
                value={sortingOrder}
                onChange={(e) => setSortingOrder(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    height: { sm: '42px' }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2"
            sx={{
              mb: 0.5,
              display: 'block',
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            FILTER BY SEGMENTS
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: { xs: 2, sm: 4 },
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: '6px',
              padding: { xs: '8px', sm: '12px' },
              minHeight: { sm: '52px' },
              alignItems: 'center'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={nseChecked}
                  onChange={(e) => handleSegmentChange(setNseChecked, e.target.checked)}
                  sx={{
                    padding: { xs: '4px', sm: '8px' },
                    '&.Mui-checked': { color: '#1976d2' }
                  }}
                />
              }
              label="NSE"
              sx={{
                marginRight: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={mcxChecked}
                  onChange={(e) => handleSegmentChange(setMcxChecked, e.target.checked)}
                  sx={{
                    padding: { xs: '4px', sm: '8px' },
                    '&.Mui-checked': { color: '#1976d2' }
                  }}
                />
              }
              label="MCX"
              sx={{
                marginRight: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
          </Box>
        </Box>

        {segmentError && (
          <Typography
            color="error"
            sx={{
              mb: 2,
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '8px',
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              borderRadius: '8px',
            }}
          >
            At least one segment must be selected.
          </Typography>
        )}

        {apicallFail ? (
          <Typography
            color="error"
            sx={{
              mb: 2,
              fontSize: '0.75rem',
              textAlign: 'center',
              padding: '8px',
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              borderRadius: '8px',
            }}
          >
            Unable to filter trades data, please contact support.
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              textAlign: 'center',
              fontSize: '0.75rem',
              color: 'text.secondary',
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              padding: '12px',
              borderRadius: '8px',
            }}
          >
            If a date range is not passed, the filter will apply only to this week's trades.
          </Typography>
        )}      <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: { xs: 1, sm: 1.5 },
            mt: { xs: 2, sm: 3 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilter}
            size={window.innerWidth > 600 ? 'medium' : 'small'}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: { xs: '80px', sm: '100px' },
              height: { sm: '42px' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetFilter}
            size={window.innerWidth > 600 ? 'medium' : 'small'}
            sx={{
              flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
              minWidth: { xs: '80px', sm: '100px' },
              height: { sm: '42px' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            size={window.innerWidth > 600 ? 'medium' : 'small'}
            sx={{
              flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
              minWidth: { xs: '80px', sm: '100px' },
              height: { sm: '42px' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >Close
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TradeHistoryFilterCard;
